import React from 'react';
import { Link } from 'react-router-dom';

const JobBidsModal = ({
  job,
  bids,
  loading,
  processingBid,
  onClose,
  onAcceptBid,
  onRejectBid,
  onMessageProvider,
  onAskQuestion
}) => {
  if (!job) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="job-bids-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="flex justify-between items-start">
            <div className="text-white">
              <h2 className="text-xl font-bold">Bids on Your Job</h2>
              <p className="text-white text-opacity-90 text-sm mt-1">{job.title}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:opacity-80"
              data-testid="close-bids-modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {/* Job Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Your Budget</p>
                <p className="text-xl font-bold text-blue-600">₱{job.budget?.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Status</p>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  job.status === 'open' ? 'bg-green-100 text-green-700' :
                  job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {job.status}
                </span>
              </div>
            </div>
            {/* Preferred Schedule */}
            {(job.preferred_date || job.preferred_start_time || job.preferred_end_time) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Your Preferred Schedule:</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {job.preferred_date && (
                    <span className="flex items-center gap-1 text-gray-700">
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(job.preferred_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {(job.preferred_start_time || job.preferred_end_time) && (
                    <span className="flex items-center gap-1 text-gray-700">
                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {job.preferred_start_time && job.preferred_end_time 
                        ? `${job.preferred_start_time} - ${job.preferred_end_time}`
                        : job.preferred_start_time || job.preferred_end_time}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bids List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading bids...</p>
            </div>
          ) : bids.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No bids received yet</p>
              <p className="text-sm text-gray-400 mt-2">Providers will see your job in Browse Jobs</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">{bids.length} bid{bids.length !== 1 ? 's' : ''} received</p>
              
              {bids.map((bid) => (
                <div
                  key={bid.id}
                  className={`border rounded-lg p-4 ${
                    bid.status === 'accepted' ? 'border-green-500 bg-green-50' :
                    bid.status === 'rejected' ? 'border-gray-300 bg-gray-50 opacity-60' :
                    'border-gray-200 hover:shadow-md'
                  } transition`}
                  data-testid={`bid-card-${bid.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold text-gray-800">{bid.provider_name}</h4>
                      <p className="text-sm text-gray-500">
                        Submitted {new Date(bid.created_at).toLocaleDateString('en-PH', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-blue-600">₱{bid.bid_amount?.toLocaleString()}</p>
                      {bid.estimated_duration && (
                        <p className="text-sm text-gray-500">Est: {bid.estimated_duration}</p>
                      )}
                      {bid.proposed_time && (
                        <p className="text-sm text-gray-500">
                          <span className="text-gray-400">Start:</span> {bid.proposed_time}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Bid Message */}
                  <div className="bg-white rounded p-3 mb-3 border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{bid.message}</p>
                  </div>

                  {/* View Provider Profile Link */}
                  <div className="mb-3 flex items-center gap-3">
                    <Link
                      to={`/provider/${bid.provider_id}`}
                      className="inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 font-medium"
                      data-testid={`view-provider-${bid.provider_id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      View Profile
                    </Link>
                    
                    {/* Ask Question button for pending bids */}
                    {bid.status === 'pending' && (
                      <button
                        onClick={() => onAskQuestion(bid)}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        data-testid={`ask-bidder-${bid.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Ask Question
                      </button>
                    )}
                  </div>

                  {/* Status Badge or Action Buttons */}
                  {bid.status === 'accepted' ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-600 font-semibold">
                        <span>✓</span> Accepted
                      </div>
                      <button
                        onClick={() => onMessageProvider(bid)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:shadow-md transition text-sm font-medium"
                        data-testid={`message-provider-${bid.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Message Provider
                      </button>
                    </div>
                  ) : bid.status === 'rejected' ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <span>✗</span> Rejected
                    </div>
                  ) : job.status === 'open' ? (
                    <div className="flex gap-3">
                      <button
                        onClick={() => onAcceptBid(bid.id)}
                        disabled={processingBid === bid.id}
                        className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 font-semibold disabled:opacity-50"
                        data-testid={`accept-bid-${bid.id}`}
                      >
                        {processingBid === bid.id ? 'Processing...' : '✓ Accept Bid'}
                      </button>
                      <button
                        onClick={() => onRejectBid(bid.id)}
                        disabled={processingBid === bid.id}
                        className="flex-1 border border-red-500 text-red-500 py-2 rounded-lg hover:bg-red-50 font-semibold disabled:opacity-50"
                        data-testid={`reject-bid-${bid.id}`}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Job is no longer accepting bids
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobBidsModal;
